import Api from 'entities/helpers/api';
import ErrorLogger from 'entities/helpers/error-log-helper';
import { normalizeDateRange } from './filter-search-helper';

const RECIPIENT_LOOKUP_LIMIT = 500;

/**
 * Fetch recipient IDs for name filtering (searches by email address only)
 * @param {Array} selectedNames - Array of name objects with email addresses
 * @returns {Promise<Array>} Array of recipient IDs
 */
const fetchNameFilterRecipientIds = async (selectedNames) => {
  const recipientPromises = selectedNames.flatMap((recipient) => {
    const { emailAddress: email_addresses } = recipient;
    // Handle both single email address (string) and multiple email addresses (array)
    const emailArray = Array.isArray(email_addresses) ? email_addresses : [email_addresses];

    // Create a separate API call for each email address
    return emailArray.map(async (email_address) => {
      const response = await Api.OUTREACH.EMAIL_RECIPIENTS.post({
        data: JSON.stringify({
          email_address,
          recipient_types: ['TO', 'CC', 'BCC'],
        }),
        params: {
          limit: RECIPIENT_LOOKUP_LIMIT,
        },
        error_message: 'Failed to fetch recipient ID',
      });

      return response.items?.map((item) => item.id) || [];
    });
  });

  const recipientIdArrays = await Promise.all(recipientPromises);
  return recipientIdArrays.flat();
};

/**
 * Common email search utility that handles filtering logic for drafts, scheduled, and sent emails
 */
export const createEmailSearchFunction = (emailType, user, setItems, setTotal, page, limit, setLoading) => {
  return async ({ dateRange, replyStatus, replyDateRange, emailRecipients, selectedNames } = {}) => {
    setLoading(true);
    try {
      // Safely normalize date ranges (doesnt break if date is undefined)
      const normalizedDateRange = normalizeDateRange(dateRange);
      const normalizedReplyDateRange = normalizeDateRange(replyDateRange);

      const fromTimestamp = normalizedDateRange?.from;
      const toTimestamp = normalizedDateRange?.to;
      const replyFromTimestamp = normalizedReplyDateRange?.from;
      const replyToTimestamp = normalizedReplyDateRange?.to;

      // Check if any filter is present
      const hasDateFilter = fromTimestamp && toTimestamp && fromTimestamp <= toTimestamp;
      const hasReplyDateFilter = replyFromTimestamp && replyToTimestamp && replyFromTimestamp <= replyToTimestamp;
      const hasReplyStatusFilter = replyStatus && replyStatus !== '';
      const hasRecipientFilter = emailRecipients && emailRecipients.length > 0;
      const hasNameFilter = selectedNames && selectedNames.length > 0;

      //-------- API THINGS --------//
      // Always create payload with email type filter, even if no other filters are present
      const payload = {
        email: {
          creator_user_id: user.id,
        },
      };

      // Add status filter based on email type
      if (emailType === 'draft') {
        payload.email.statuses = ['DRAFT'];
      } else if (emailType === 'scheduled') {
        payload.email.statuses = ['SCHEDULED'];
      } else if (emailType === 'sent') {
        payload.email.statuses = ['SENT'];
      }

      // Only proceed with API call if there are actual filters or if we need to ensure email type filtering
      if (hasDateFilter || hasReplyDateFilter || hasReplyStatusFilter || hasRecipientFilter || hasNameFilter) {
        // Add date range filters
        if (hasDateFilter || hasReplyDateFilter) {
          if (emailType === 'draft') {
            payload.created_at_start = fromTimestamp;
            payload.created_at_end = toTimestamp;
          } else if (emailType === 'scheduled') {
            payload.scheduled_at_start = fromTimestamp;
            payload.scheduled_at_end = toTimestamp;
          } else if (emailType === 'sent') {
            if (hasDateFilter) {
              payload.sent_at_start = fromTimestamp;
              payload.sent_at_end = toTimestamp;
              payload.email.statuses = ['SENT'];
            } else if (hasReplyDateFilter) {
              payload.sent_at_start = replyFromTimestamp;
              payload.sent_at_end = replyToTimestamp;
              payload.email.statuses = ['REPLIED'];
            }
          }
        }

        // Add reply status filter (only for sent emails)
        if (hasReplyStatusFilter && emailType === 'sent') {
          payload.reply_filter_option = replyStatus.value;
          if (replyStatus.value === 'USER_LAST_TO_REPLY' || replyStatus.value === 'NO_REPLIES') {
            payload.email.statuses = ['SENT'];
          }
          if (replyStatus.value === 'RECIPIENT_LAST_TO_REPLY') {
            payload.email.statuses = ['REPLIED'];
          }
        }

        // Collect recipient IDs from both name and email filters
        let allRecipientIds = [];

        if (hasNameFilter) {
          try {
            const nameRecipientIds = await fetchNameFilterRecipientIds(selectedNames);
            // If no recipient IDs were found, set empty results and return early
            if (nameRecipientIds.length === 0) {
              setItems([]);
              setTotal(0);
              return; // Exit the entire function early
            }

            allRecipientIds.push(...nameRecipientIds);
          } catch (error) {
            ErrorLogger.error('Error fetching name filter emails', {
              extra: { error, page_name: `Outreach ${emailType} name filter select` },
            });
            // On error, set empty results and return early
            setItems([]);
            setTotal(0);
            return; // Exit the entire function early
          }
        }

        // Add email recipients filter
        if (hasRecipientFilter) {
          try {
            const recipientPromises = emailRecipients.map(async (recipient) => {
              const { emailAddress: email_address } = recipient;

              const response = await Api.OUTREACH.EMAIL_RECIPIENTS.post({
                data: JSON.stringify({
                  email_address,
                  recipient_types: ['TO', 'CC', 'BCC'],
                }),
                params: {
                  limit: 500,
                },
                error_message: 'Failed to fetch recipient ID',
              });
              return response.items?.map((item) => item.id) || [];
            });

            const recipientIdArrays = await Promise.all(recipientPromises);
            const emailRecipientIds = recipientIdArrays.flat();

            // If no recipient IDs were found, set empty results and return early
            if (emailRecipientIds.length === 0) {
              setItems([]);
              setTotal(0);
              return;
            }

            allRecipientIds.push(...emailRecipientIds);
          } catch (error) {
            ErrorLogger.error('Error fetching recipient emails', {
              extra: { error, page_name: `Outreach ${emailType} email filter select` },
            });
            // On error, set empty results and return early
            setItems([]);
            setTotal(0);
            return;
          }
        }

        // Combine all recipient IDs and remove duplicates
        if (allRecipientIds.length > 0) {
          const uniqueRecipientIds = [...new Set(allRecipientIds)];
          payload.email_recipient_ids = uniqueRecipientIds;
        }

        // Always make the API call to ensure email type filtering is applied
        // Determine which API endpoint to use based on email type
        let apiEndpoint;
        let successCallback;
        let errorMessage;

        if (emailType === 'draft' || emailType === 'scheduled') {
          apiEndpoint = Api.OUTREACH.EMAIL_SEARCH;
          errorMessage = `Failed to fetch ${emailType} emails`;

          successCallback = (res) => {
            const { items, total } = res;
            setItems(items);
            setTotal(total);
          };
        } else if (emailType === 'sent') {
          apiEndpoint = Api.OUTREACH.EMAIL_THREAD_SEARCH;
          errorMessage = 'Failed to fetch threads';

          successCallback = (res) => {
            const { items, total } = res;
            setItems(items);
            setTotal(total);
          };
        }

        await apiEndpoint.post({
          data: JSON.stringify(payload),
          params: {
            offset: (page - 1) * limit,
            limit: limit,
          },
          error_message: errorMessage,
          success: successCallback,
        });
      }
    } catch (error) {
      ErrorLogger.error(`Error fetching ${emailType} emails`, {
        extra: { error, page_name: `Email search page - ${emailType}` },
      });
    } finally {
      setLoading(false);
    }
  };
};
